import { App } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFolderTree } from "@devowl-wp/react-folder-tree";
import { bindAttachmentFilterListener, isFilterActive, toggleSortable } from "../../../backbone/attachmentSortable.js";
import { useAppTreeControllerRef } from "../../../contexts/appTreeControllerContext.js";
import { useAppTreeLocalState } from "../../../contexts/appTreeLocalStateContext.js";
import { useAppTreeMountContext } from "../../../contexts/appTreeMountContext.js";
import { __ } from "../../../utils/i18n.js";
import { rmlOpts } from "../../../utils/rmlOpts.js";
/**
 * Order-mode handlers shared by the tree body, mount, and media bridge.
 * Only the tree body passes `bindFilterListener: true` so the Backbone filter
 * listener is registered once per tree (not three times via the other callers).
 */ function useOrderHandlers(options) {
    const bindFilterListener = options?.bindFilterListener ?? false;
    const { message } = App.useApp();
    const { set } = useFolderTree();
    const controllerRef = useAppTreeControllerRef();
    const { attachmentsBrowser } = useAppTreeMountContext();
    const { isWPAttachmentsSortMode, setIsMoveable, setIsWPAttachmentsSortMode, setShowProFeature } = useAppTreeLocalState();
    const [attachmentFilterActive, setAttachmentFilterActive] = useState(()=>isFilterActive(attachmentsBrowser));
    const sortModeRef = useRef(isWPAttachmentsSortMode);
    sortModeRef.current = isWPAttachmentsSortMode;
    const exitSortMode = useCallback(()=>{
        toggleSortable(controllerRef.current.getTreeItemById(), false, attachmentsBrowser, message);
        setIsMoveable(true);
        setIsWPAttachmentsSortMode(false);
        set({
            toolbarActiveButton: undefined
        });
    }, [
        attachmentsBrowser,
        controllerRef,
        message,
        set,
        setIsMoveable,
        setIsWPAttachmentsSortMode
    ]);
    useEffect(()=>{
        if (!bindFilterListener) {
            return;
        }
        return bindAttachmentFilterListener(attachmentsBrowser, (filterActive)=>{
            setAttachmentFilterActive(filterActive);
            if (!filterActive) {
                return;
            }
            if (sortModeRef.current) {
                exitSortMode();
                message.error(__("In the current view of uploads, filters are active. Please reset them and refresh the view."));
                return;
            }
            toggleSortable(controllerRef.current.getTreeItemById(), false, attachmentsBrowser, message);
        });
    }, [
        attachmentsBrowser,
        bindFilterListener,
        controllerRef,
        exitSortMode,
        message
    ]);
    const handleOrderClick = useCallback(()=>{
        if (!rmlOpts.others.isPro && rmlOpts.others.showProHints) {
            setShowProFeature("order-content");
        } else {
            const node = controllerRef.current.getTreeItemById();
            if (toggleSortable(node, true, attachmentsBrowser, message)) {
                setIsMoveable(false);
                setIsWPAttachmentsSortMode(true);
                set({
                    toolbarActiveButton: "order"
                });
            }
        }
    }, [
        attachmentsBrowser,
        controllerRef,
        message,
        set,
        setIsMoveable,
        setIsWPAttachmentsSortMode,
        setShowProFeature
    ]);
    const handleOrderCancel = useCallback(()=>{
        exitSortMode();
    }, [
        exitSortMode
    ]);
    return {
        attachmentFilterActive,
        handleOrderClick,
        handleOrderCancel
    };
}
export { useOrderHandlers };
